import type { SocialPostDto } from "@bafe/api-client";
import { Select } from "@bafe/ui";
import { SOCIAL_NETWORKS } from "@bafe/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Popup } from "../../components/Popup";
import { api } from "../../lib/api";
import { presentError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";
import { isoDay, labelOf, stamp } from "../../lib/when.js";

function gridDay(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function monthCells(cursor: Date): string[] {
	const year = cursor.getFullYear();
	const month = cursor.getMonth();
	const today = gridDay(new Date());
	const first = new Date(year, month, 1);
	const lead = (first.getDay() + 6) % 7;
	const count = new Date(year, month + 1, 0).getDate();
	const cells = Array.from({ length: lead + count }, (_, index) => gridDay(new Date(year, month, index - lead + 1)));
	while (cells.length % 7) {
		const last = cells[cells.length - 1] ?? today;
		const date = new Date(`${last}T12:00:00`);
		date.setDate(date.getDate() + 1);
		cells.push(gridDay(date));
	}
	return cells;
}

function postDay(post: SocialPostDto): string {
	return isoDay(post.scheduledAt ?? post.createdAt);
}

function postTone(status: string): string {
	if (status === "scheduled") return "ev-task";
	if (status === "failed") return "ev-failed";
	return "ev-event";
}

export function SocialOverview() {
	const query = useQuery({ queryKey: queryKeys.socialOverview, queryFn: () => api.getSocialOverview() });
	if (query.isPending) return <p className="empty">Loading Social.</p>;
	if (query.isError) return <p className="err">{presentError(query.error).title}</p>;
	if (!query.data || query.data.channelCount === 0) {
		return (
			<p className="empty">
				No brand accounts connected. <Link to="/social/channels">Connect a channel</Link> to schedule posts.
			</p>
		);
	}
	return (
		<div className="stats">
			<Link className="stat" to="/social/channels">
				<strong>{query.data.channelCount}</strong>
				<span>Channels</span>
			</Link>
			<Link className="stat" to="/social/calendar">
				<strong>{query.data.scheduledCount}</strong>
				<span>Scheduled</span>
			</Link>
		</div>
	);
}

export function SocialChannels() {
	const query = useQuery({ queryKey: queryKeys.socialChannels, queryFn: () => api.listSocialChannels() });
	const overview = useQuery({ queryKey: queryKeys.socialOverview, queryFn: () => api.getSocialOverview() });
	const queryClient = useQueryClient();
	const [params] = useSearchParams();
	const connected = params.get("connected");
	const [open, setOpen] = useState(false);
	const [handle, setHandle] = useState("");
	const [network, setNetwork] = useState<(typeof SOCIAL_NETWORKS)[number]>("instagram");
	const [error, setError] = useState<string | null>(null);
	const live = overview.data?.driver === "postiz";
	const native = overview.data?.driver === "key";
	const selection = params.get("facebook");
	const facebookError = params.get("facebook_error");
	const [pageIds, setPageIds] = useState<string[]>([]);
	const claimed = useRef(false);
	const pages = useQuery({
		queryKey: queryKeys.socialFacebookPages(selection ?? ""),
		queryFn: () => api.listFacebookPages(selection ?? ""),
		enabled: native && Boolean(selection),
	});

	const claim = useMutation({
		mutationFn: () => api.claimSocialChannels(),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialChannels });
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialOverview });
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	useEffect(() => {
		if (connected !== "1" || !live || claimed.current) return;
		claimed.current = true;
		claim.mutate();
	}, [connected, live, claim]);

	const connect = useMutation({
		mutationFn: () => api.connectSocialChannel(native ? "facebook" : network, live || native ? undefined : handle.trim()),
		onSuccess: async (result) => {
			setOpen(false);
			setHandle("");
			if (result.url) {
				window.location.assign(result.url);
				return;
			}
			await queryClient.invalidateQueries();
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const savePages = useMutation({
		mutationFn: () => api.confirmFacebookPages(selection ?? "", pageIds),
		onSuccess: async () => {
			setPageIds([]);
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialChannels });
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialOverview });
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const disconnect = useMutation({
		mutationFn: (id: string) => api.disconnectSocialChannel(id),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialChannels });
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialOverview });
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	if (query.isPending) return <p className="empty">Loading channels.</p>;
	if (query.isError) return <p className="err">{presentError(query.error).title}</p>;

	return (
		<div>
			<p className="sub">
				{native
					? "Connect a Facebook Page you manage. The Page token stays encrypted on Key, and this browser never sees it."
					: live
						? "Sign in on the network. When that finishes, come back and check for the new account. Tokens stay off this browser."
						: "Facebook, Instagram, Threads, X, LinkedIn and TikTok. This workspace is storing posts locally."}
			</p>
			{facebookError ? (
				<p className="err">
					{facebookError === "denied"
						? "Facebook did not grant access. Connect the Page again."
						: facebookError === "pages"
							? "No Facebook Page you can post to was found."
							: facebookError === "unavailable"
								? "Facebook could not be reached. Try again."
								: "That connection expired. Connect the Page again."}
				</p>
			) : null}
			{error && <p className="err">{error}</p>}
			<div className="row head-actions">
				<button className="btn" type="button" onClick={() => setOpen(true)}>
					Connect channel
				</button>
				{live ? (
					<button className="btn ghost" type="button" onClick={() => claim.mutate()} disabled={claim.isPending}>
						{claim.isPending ? "Checking" : "Check for new accounts"}
					</button>
				) : null}
			</div>
			{!query.data?.length ? (
				<p className="empty">No channels yet. Connect a brand account to start publishing.</p>
			) : (
				<div className="stats">
					{query.data.map((channel) => (
						<div className="stat" key={channel.id}>
							<strong>{labelOf(channel.network)}</strong>
							<span>{channel.handle}</span>
							{channel.lastError ? <span>{channel.lastError}</span> : null}
							<button className="btn ghost" type="button" onClick={() => disconnect.mutate(channel.id)}>
								Disconnect
							</button>
						</div>
					))}
				</div>
			)}
			{native && selection ? (
				<div className="card">
					{pages.isPending ? <p className="empty">Loading Pages.</p> : null}
					{pages.isError ? <p className="err">{presentError(pages.error).title}</p> : null}
					{pages.data ? (
						<form
							onSubmit={(event: FormEvent) => {
								event.preventDefault();
								savePages.mutate();
							}}
						>
							<fieldset className="checks">
								<legend>Choose Pages</legend>
								{pages.data.map((page) => (
									<label key={page.id} className="check">
										<input
											type="checkbox"
											checked={pageIds.includes(page.id)}
											onChange={() =>
												setPageIds((current) =>
													current.includes(page.id) ? current.filter((id) => id !== page.id) : [...current, page.id],
												)
											}
										/>
										<span>{page.name}</span>
									</label>
								))}
							</fieldset>
							<button className="btn" type="submit" disabled={savePages.isPending || pageIds.length === 0}>
								{savePages.isPending ? "Saving" : "Add Pages"}
							</button>
						</form>
					) : null}
				</div>
			) : null}
			<Popup open={open} title="Connect channel" onClose={() => setOpen(false)}>
				<form
					onSubmit={(event: FormEvent) => {
						event.preventDefault();
						connect.mutate();
					}}
				>
					<label>
						Network
						<Select value={native ? "facebook" : network} onChange={(event) => setNetwork(event.target.value as typeof network)}>
							{(native ? (["facebook"] as const) : SOCIAL_NETWORKS).map((item) => (
								<option key={item} value={item}>
									{labelOf(item)}
								</option>
							))}
						</Select>
					</label>
					{live || native ? null : (
						<label>
							Account
							<input value={handle} onChange={(event) => setHandle(event.target.value)} required maxLength={80} />
						</label>
					)}
					<button className="btn" type="submit" disabled={connect.isPending}>
						{connect.isPending ? "Connecting" : "Connect"}
					</button>
				</form>
			</Popup>
		</div>
	);
}

export function SocialCalendar() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [cursor, setCursor] = useState(() => new Date());
	const [view, setView] = useState<"month" | "agenda">("month");
	const [network, setNetwork] = useState<string>("all");
	const cells = useMemo(() => monthCells(cursor), [cursor]);
	const startDay = cells[0] ?? gridDay(cursor);
	const endDay = cells[cells.length - 1] ?? startDay;
	const start = new Date(`${startDay}T00:00:00`).toISOString();
	const end = new Date(`${endDay}T23:59:59`).toISOString();
	const query = useQuery({
		queryKey: [...queryKeys.socialPosts, start, end],
		queryFn: () => api.listSocialPosts(start, end),
	});
	const remove = useMutation({
		mutationFn: (id: string) => api.deleteSocialPost(id),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialPosts });
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialOverview });
		},
	});

	const year = cursor.getFullYear();
	const month = cursor.getMonth();
	const label = cursor.toLocaleString("en-GB", { month: "long", year: "numeric" });
	const today = gridDay(new Date());
	const selected = gridDay(cursor);
	const networks = useMemo(() => {
		const found = new Set<string>();
		for (const post of query.data ?? []) {
			for (const item of post.networks) found.add(item);
		}
		return [...found];
	}, [query.data]);

	function itemsOn(value: string): SocialPostDto[] {
		return (query.data ?? []).filter((post) => {
			if (postDay(post) !== value) return false;
			if (network !== "all" && !post.networks.includes(network)) return false;
			return true;
		});
	}

	function composeOn(day: string) {
		navigate(`/social/compose?day=${day}`);
	}

	if (query.isPending) return <p className="empty">Loading calendar.</p>;
	if (query.isError) return <p className="err">{presentError(query.error).title}</p>;

	return (
		<div className="cal-page">
			<aside className="cal-side">
				<div>
					<div className="mini-top">
						<span>{label}</span>
						<div className="mini-nav">
							<button type="button" aria-label="Previous" onClick={() => setCursor(new Date(year, month - 1, 1))}>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
									<path d="m15 18-6-6 6-6" />
								</svg>
							</button>
							<button type="button" aria-label="Next" onClick={() => setCursor(new Date(year, month + 1, 1))}>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
									<path d="m9 18 6-6-6-6" />
								</svg>
							</button>
						</div>
					</div>
					<div className="mini-grid">
						{["M", "T", "W", "T", "F", "S", "S"].map((name, index) => (
							<div className="dow" key={`${name}${index}`}>
								{name}
							</div>
						))}
						{cells.map((value) => (
							<button
								type="button"
								key={value}
								className={[value.slice(0, 7) !== selected.slice(0, 7) ? "out" : "", value === today ? "today" : "", value === selected ? "on" : ""]
									.filter(Boolean)
									.join(" ")}
								onClick={() => setCursor(new Date(`${value}T12:00:00`))}
							>
								{Number(value.slice(8))}
							</button>
						))}
					</div>
				</div>
				<div>
					<p className="sub">Networks</p>
					<button type="button" className={network === "all" ? "cal-src on" : "cal-src"} onClick={() => setNetwork("all")}>
						<span className="dot dot-event" />
						All
					</button>
					{networks.map((item) => (
						<button
							type="button"
							key={item}
							className={network === item ? "cal-src on" : "cal-src"}
							onClick={() => setNetwork(item)}
						>
							<span className="dot dot-task" />
							{labelOf(item)}
						</button>
					))}
				</div>
			</aside>
			<div>
				<div className="cal-bar">
					<div className="actions">
						<button className="btn ghost" type="button" onClick={() => setCursor(new Date())}>
							Today
						</button>
						<div className="navs">
							<button className="cal-step" type="button" aria-label="Previous" onClick={() => setCursor(new Date(year, month - 1, 1))}>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
									<path d="m15 18-6-6 6-6" />
								</svg>
							</button>
							<button className="cal-step" type="button" aria-label="Next" onClick={() => setCursor(new Date(year, month + 1, 1))}>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
									<path d="m9 18 6-6-6-6" />
								</svg>
							</button>
						</div>
						<h1>{label}</h1>
					</div>
					<div className="actions">
						<nav className="seg">
							<button type="button" className={view === "month" ? "active" : ""} onClick={() => setView("month")}>
								Month
							</button>
							<button type="button" className={view === "agenda" ? "active" : ""} onClick={() => setView("agenda")}>
								Agenda
							</button>
						</nav>
						<button className="btn" type="button" onClick={() => composeOn(selected)}>
							Compose
						</button>
					</div>
				</div>
				{view === "month" ? (
					<div className="cal-month">
						<div className="cal-month-head">
							{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((name) => (
								<div className="cal-dow" key={name}>
									{name}
								</div>
							))}
						</div>
						<div className="cal-month-body">
							{cells.map((value) => {
								const rows = itemsOn(value);
								return (
									<div
										className={["cal-cell", value.slice(0, 7) !== selected.slice(0, 7) ? "out" : "", value === today ? "today" : ""]
											.filter(Boolean)
											.join(" ")}
										key={`cell-${value}`}
									>
										<div className="cal-cell-top">
											<button type="button" className="num" onClick={() => setCursor(new Date(`${value}T12:00:00`))}>
												{Number(value.slice(8))}
											</button>
											<button type="button" className="cal-add" aria-label="Compose post" onClick={() => composeOn(value)}>
												+
											</button>
										</div>
										{rows.slice(0, 3).map((item) => (
											<span className={`ev ${postTone(item.status)}`} key={item.id}>
												{labelOf(item.status)} · {item.body}
											</span>
										))}
										{rows.length > 3 ? <span className="ev-more">+{rows.length - 3} more</span> : null}
									</div>
								);
							})}
						</div>
					</div>
				) : (
					<div className="cal-agenda">
						{cells
							.filter((value) => itemsOn(value).length)
							.map((value) => (
								<section key={value}>
									<h2>{value}</h2>
									{itemsOn(value).map((item) => (
										<div className="cal-line" key={item.id}>
											<div className="when">{stamp(item.scheduledAt ?? item.createdAt)}</div>
											<div>
												<div>{item.body}</div>
												<div className="who">
													{labelOf(item.status)} · {item.networks.map(labelOf).join(", ")}
													{item.releaseUrl ? (
														<>
															{" "}
															· <a href={item.releaseUrl}>View post</a>
														</>
													) : null}
													{item.error ? ` · ${item.error}` : ""}
												</div>
												<button className="btn ghost" type="button" onClick={() => remove.mutate(item.id)}>
													Delete
												</button>
											</div>
										</div>
									))}
								</section>
							))}
						{cells.every((value) => itemsOn(value).length === 0) ? (
							<p className="empty">
								Nothing scheduled in this period. <Link to="/social/compose">Write a post</Link>.
							</p>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
}

export function SocialCompose() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const channels = useQuery({ queryKey: queryKeys.socialChannels, queryFn: () => api.listSocialChannels() });
	const overview = useQuery({ queryKey: queryKeys.socialOverview, queryFn: () => api.getSocialOverview() });
	const live = overview.data?.driver === "postiz";
	const native = overview.data?.driver === "key";
	const day = params.get("day");
	const [body, setBody] = useState("");
	const [scheduledAt, setScheduledAt] = useState(day ? `${day}T09:00` : "");
	const [channelIds, setChannelIds] = useState<string[]>([]);
	const [files, setFiles] = useState<File[]>([]);
	const [draft, setDraft] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const publish = useMutation({
		mutationFn: async () => {
			const images: Array<{ id: string; path: string }> = [];
			if (live || native) {
				for (const file of files.slice(0, native ? 1 : files.length)) images.push(await api.uploadSocialMedia(file));
			}
			return api.createSocialPost({
				body: body.trim(),
				channelIds,
				scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
				mode: draft ? "draft" : scheduledAt ? "schedule" : "now",
				images,
			});
		},
		onSuccess: async () => {
			setBody("");
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialPosts });
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialOverview });
			navigate("/social/calendar");
		},
		onError: async (caught) => {
			setError(presentError(caught).title);
			await queryClient.invalidateQueries({ queryKey: queryKeys.socialPosts });
		},
	});

	if (channels.isPending) return <p className="empty">Loading composer.</p>;
	if (channels.isError) return <p className="err">{presentError(channels.error).title}</p>;
	if (!channels.data?.length) {
		return (
			<p className="empty">
				Connect a channel first. <Link to="/social/channels">Open channels</Link>.
			</p>
		);
	}

	function toggle(item: string) {
		setChannelIds((current) => (current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item]));
	}

	return (
		<form
			className="card"
			onSubmit={(event) => {
				event.preventDefault();
				setError(null);
				publish.mutate();
			}}
		>
			<p className="sub">
				{native
					? "The browser never holds the Page token. Publishing goes through Key to Facebook."
					: "The browser never holds the network tokens. Publishing goes through Key, then Postiz."}
			</p>
			{error && <p className="err">{error}</p>}
			<label>
				Post
				<textarea value={body} onChange={(event) => setBody(event.target.value)} required maxLength={4000} rows={6} />
			</label>
			<fieldset className="checks">
				<legend>Networks</legend>
				{channels.data.map((channel) => (
					<label key={channel.id} className="check">
						<input type="checkbox" checked={channelIds.includes(channel.id)} onChange={() => toggle(channel.id)} />
						<span>
							{labelOf(channel.network)} · {channel.handle}
						</span>
					</label>
				))}
			</fieldset>
			<label>
				Schedule
				<input type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} />
			</label>
			{live || native ? (
				<label>
					{native ? "Photo" : "Photos or video"}
					<input
						type="file"
						accept={native ? "image/jpeg,image/png,image/gif,image/webp" : "image/jpeg,image/png,image/gif,image/webp,video/mp4"}
						multiple={!native}
						onChange={(event) => setFiles([...(event.target.files ?? [])].slice(0, native ? 1 : 10))}
					/>
				</label>
			) : null}
			<label className="check">
				<input type="checkbox" checked={draft} onChange={(event) => setDraft(event.target.checked)} />
				<span>Save as a draft</span>
			</label>
			<button className="btn" type="submit" disabled={publish.isPending || channelIds.length === 0}>
				{publish.isPending ? "Saving" : draft ? "Save draft" : scheduledAt ? "Schedule" : "Publish"}
			</button>
		</form>
	);
}

export function SocialResults() {
	const channels = useQuery({ queryKey: queryKeys.socialChannels, queryFn: () => api.listSocialChannels() });
	const overview = useQuery({ queryKey: queryKeys.socialOverview, queryFn: () => api.getSocialOverview() });
	const [channelId, setChannelId] = useState("");
	const [days, setDays] = useState("30");
	const selected = channelId || channels.data?.[0]?.id || "";
	const metrics = useQuery({
		queryKey: [...queryKeys.socialAnalytics, selected, days],
		queryFn: () => api.socialAnalytics(selected, Number(days)),
		enabled: overview.data?.driver === "postiz" && Boolean(selected),
	});

	if (channels.isPending || overview.isPending) return <p className="empty">Loading results.</p>;
	if (channels.isError) return <p className="err">{presentError(channels.error).title}</p>;
	if (overview.data?.driver === "key") {
		return <p className="empty">Open a published post from the calendar to view it on Facebook.</p>;
	}
	if (overview.data?.driver !== "postiz") {
		return <p className="empty">Results appear after this workspace publishes through Postiz.</p>;
	}
	if (!channels.data?.length) {
		return (
			<p className="empty">
				No brand accounts connected. <Link to="/social/channels">Connect a channel</Link>.
			</p>
		);
	}

	return (
		<div>
			<div className="row head-actions">
				<label>
					Account
					<Select value={selected} onChange={(event) => setChannelId(event.target.value)}>
						{channels.data.map((channel) => (
							<option key={channel.id} value={channel.id}>
								{labelOf(channel.network)} · {channel.handle}
							</option>
						))}
					</Select>
				</label>
				<label>
					Period
					<Select value={days} onChange={(event) => setDays(event.target.value)}>
						<option value="7">7 days</option>
						<option value="30">30 days</option>
						<option value="90">90 days</option>
					</Select>
				</label>
			</div>
			{metrics.isPending ? <p className="empty">Loading results.</p> : null}
			{metrics.isError ? <p className="err">{presentError(metrics.error).title}</p> : null}
			{metrics.data?.length ? (
				<div className="stats">
					{metrics.data.map((metric) => (
						<div className="stat" key={metric.label}>
							<strong>{metric.latest}</strong>
							<span>
								{metric.label} · {metric.percentageChange}%
							</span>
						</div>
					))}
				</div>
			) : metrics.isSuccess ? (
				<p className="empty">No results for this period.</p>
			) : null}
		</div>
	);
}
